import { inject } from "@angular/core";
import { type Route, Router } from "@angular/router";

import { checkIfLoggedInFn, NotArchivedPlan, notPmoEntity } from "../../core/guards";
import { isDsV1, isDsV2, isDsV3 } from "../../core/utils";
import { Constants } from "../../shared/constants/constants";
import { PageMode } from "../../shared/constants/enums";
import { projectsGuards, strategicProjectGuards, submenuGuards } from "./_guards";

const strategicProjectRoutes: Route = {
  path: "strategic-projects",
  canActivate: [
    checkIfLoggedInFn,
    () => {
      const router = inject(Router);

      if (isDsV2()) return true;
      else if (isDsV1()) router.navigate(["/console", "initiatives", "list"]);
      return router.navigate(["/console", "project"]);
    },
  ],
  children: [
    {
      path: "",
      canActivate: [...strategicProjectGuards.view],
      data: {
        title: "breadcrumbs.initiative_module.strategic_project_list",
        breadcrumb: "breadcrumbs.initiative_module.strategic_project_list",
        initiativeType: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
        itemTypeId: Constants.CONSTANT_PERMISSIONS.STRATEGIC_PROJECT,
      },
      loadComponent: () => import("./projects-list/projects-list").then((m) => m.ProjectsListComponent),
    },
    {
      path: "create",
      canActivate: [notPmoEntity, ...strategicProjectGuards.create, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.initiative_module.strategic_project_new",
        breadcrumb: "breadcrumbs.initiative_module.strategic_project_new",
        initiativeType: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
        pageMode: PageMode.create,
      },
      loadComponent: () => import("./project-editor/project-editor").then((m) => m.ProjectEditorComponent),
    },
    {
      path: "edit/:id",
      canActivate: [...strategicProjectGuards.edit, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.initiative_module.strategic_project_edit",
        breadcrumb: "breadcrumbs.initiative_module.strategic_project_edit",
        initiativeType: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
        pageMode: PageMode.edit,
      },
      loadComponent: () => import("./project-editor/project-editor").then((m) => m.ProjectEditorComponent),
    },
    {
      path: ":id",
      canActivate: [...strategicProjectGuards.view],
      data: {
        title: "initiatives.strategic_project",
        breadcrumb: "breadcrumbs.initiative_module.strategic_project_view",
        type: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
      },
      loadComponent: () => import("./project-view/project-view").then((m) => m.ProjectViewComponent),
    },
  ],
};

const regularTaskRoutes: Route = {
  path: "regular-tasks",
  canActivate: [
    checkIfLoggedInFn,
    () => {
      const router = inject(Router);

      if (isDsV2()) return true;
      else if (isDsV1()) router.navigate(["/console", "initiatives", "list"]);
      return router.navigate(["/console", "project"]);
    },
  ],
  children: [
    {
      path: "",
      canActivate: [...projectsGuards.view],
      data: {
        title: "breadcrumbs.initiative_module.regular_task_list",
        breadcrumb: "breadcrumbs.initiative_module.regular_task_list",
        initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
        itemTypeId: Constants.CONSTANT_PERMISSIONS.INITIATIVES,
      },
      loadComponent: () => import("./projects-list/projects-list").then((m) => m.ProjectsListComponent),
    },
    {
      path: "create",
      canActivate: [notPmoEntity, ...projectsGuards.create, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.initiative_module.regular_task_new",
        breadcrumb: "breadcrumbs.initiative_module.regular_task_new",
        initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
        pageMode: PageMode.create,
      },
      loadComponent: () => import("./project-editor/project-editor").then((m) => m.ProjectEditorComponent),
    },
    {
      path: "edit/:id",
      canActivate: [...projectsGuards.edit, NotArchivedPlan()],
      data: {
        title: "breadcrumbs.initiative_module.regular_task_edit",
        breadcrumb: "breadcrumbs.initiative_module.regular_task_edit",
        initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
        pageMode: PageMode.edit,
      },
      loadComponent: () => import("./project-editor/project-editor").then((m) => m.ProjectEditorComponent),
    },
    {
      path: ":id",
      canActivate: [...projectsGuards.view],
      data: {
        title: "initiatives.regular_task",
        breadcrumb: "breadcrumbs.initiative_module.regular_task_view",
        type: Constants.INITIATIVE_TYPES.REGULAR_TASK,
      },
      loadComponent: () => import("./project-view/project-view").then((m) => m.ProjectViewComponent),
    },
  ],
};

export const INITIATIVES_ROUTES: Route[] = [
  {
    path: "initiatives",
    canActivate: [checkIfLoggedInFn],
    children: [
      {
        path: "",
        canActivate: [
          checkIfLoggedInFn,
          () => {
            const router = inject(Router);

            if (isDsV2()) return true;
            else if (isDsV1()) router.navigate(["/console", "initiatives", "list"]);
            return router.navigate(["/console", "project"]);
          },
          ...submenuGuards,
        ],
        data: {
          title: "national_projects.projects",
          breadcrumb: {
            alias: "initiatives",
          },
          onCycleChange: (router: Router) => router.navigateByUrl(isDsV3() ? "console/project" : "console/initiatives"),
        },
        loadComponent: () => import("../../shared/components").then((m) => m.SubmenuComponent),
      },
      strategicProjectRoutes,
      regularTaskRoutes,
      {
        path: "list",
        canActivate: [
          checkIfLoggedInFn,
          ...projectsGuards.view,
          () => {
            const router = inject(Router);

            if (isDsV1()) return true;
            else if (isDsV2()) router.navigate(["/console", "initiatives"]);
            return router.navigate(["/console", "project"]);
          },
        ],
        data: {
          title: "breadcrumbs.initiative_module.list",
          breadcrumb: { skip: true },
          initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
          itemTypeId: Constants.CONSTANT_PERMISSIONS.INITIATIVES,
        },
        loadComponent: () => import("./projects-list/projects-list").then((m) => m.ProjectsListComponent),
      },
      {
        path: "create",
        canActivate: [notPmoEntity, checkIfLoggedInFn, ...projectsGuards.create, NotArchivedPlan()],
        data: {
          title: "breadcrumbs.initiative_module.new",
          breadcrumb: "breadcrumbs.initiative_module.new",
          initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
          pageMode: PageMode.create,
        },
        loadComponent: () => import("./project-editor/project-editor").then((m) => m.ProjectEditorComponent),
      },
      {
        path: "edit/:id",
        canActivate: [checkIfLoggedInFn, ...projectsGuards.edit, NotArchivedPlan()],
        data: {
          title: "breadcrumbs.initiative_module.edit",
          breadcrumb: "breadcrumbs.initiative_module.edit",
          initiativeType: Constants.INITIATIVE_TYPES.REGULAR_TASK,
          pageMode: PageMode.edit,
        },
        loadComponent: () => import("./project-editor/project-editor").then((m) => m.ProjectEditorComponent),
      },
      {
        path: ":id",
        canActivate: [checkIfLoggedInFn, ...projectsGuards.view],
        data: {
          title: "initiatives.initiative",
          breadcrumb: "breadcrumbs.initiative_module.view",
          type: Constants.INITIATIVE_TYPES.STRATEGIC_PROJECT,
        },
        loadComponent: () => import("./project-view/project-view").then((m) => m.ProjectViewComponent),
      },
    ],
  },
];
